/**
 * Shared dark-hero KPI primitives used by HomeLastSessionHero and SessionPerformanceHero.
 */
import React from 'react';
import { homeDarkStatTile } from '../home/homeDashboardStyles';

// ─── Win-rate donut ────────────────────────────────────────────────────────────

export function WinRateDonut({ pct, size = 40 }) {
  const r = (size - 8) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const filled = pct != null ? Math.min(100, Math.max(0, pct)) : 0;
  const offset = circ * (1 - filled / 100);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-hidden>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
      <circle
        cx={cx} cy={cx} r={r}
        fill="none"
        stroke="#38bdf8"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${cx} ${cx})`}
      />
    </svg>
  );
}

// ─── Elo delta inline label ────────────────────────────────────────────────────

export function HeroEloDelta({ delta, className = '' }) {
  if (delta == null) return <span className={`text-sm text-white/40 ${className}`}>—</span>;
  if (delta === 0) return <span className={`text-sm text-white/40 ${className}`}>±0</span>;
  if (delta > 0) {
    return <span className={`text-sm text-emerald-400 font-semibold ${className}`}>▲ {Math.abs(delta)}</span>;
  }
  return <span className={`text-sm text-rose-400 font-semibold ${className}`}>▼ {Math.abs(delta)}</span>;
}

// ─── KPI value ────────────────────────────────────────────────────────────────

export const heroKpiClass = 'text-xl sm:text-2xl font-bold tabular-nums leading-none text-white';

export function KpiWithChange({ value, change }) {
  return (
    <div className="flex items-baseline gap-1.5 sm:gap-2 min-w-0 flex-wrap">
      <span className={heroKpiClass}>{value}</span>
      {change}
    </div>
  );
}

// ─── Uniform stat tile ─────────────────────────────────────────────────────────

export function StatTile({ label, kpi, className = '' }) {
  return (
    <div className={`${homeDarkStatTile} flex flex-col min-h-[4.75rem] sm:min-h-[5.25rem] min-w-0 ${className}`}>
      <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/50 leading-none font-medium line-clamp-2">
        {label}
      </p>
      <div className="flex-1 flex items-center mt-2 min-h-[2rem]">{kpi}</div>
    </div>
  );
}

// ─── Responsive hide helpers ───────────────────────────────────────────────────

/** Hidden below md breakpoint (4th + 5th tile slots). */
export const kpiHideBelowMd = 'hidden md:flex md:flex-col';

// ─── Peg Board session tiles (home hero + session recap) ───────────────────────

export function PegBoardSessionTiles({ sessionStats }) {
  const wins = sessionStats?.wins ?? 0;
  const losses = sessionStats?.losses ?? 0;
  const totalGames = wins + losses;
  const winRatePct = sessionStats?.winRatePct ?? null;
  const maxWinStreak = sessionStats?.maxWinStreak ?? 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 items-stretch">
      <StatTile
        label="Games"
        kpi={<span className={heroKpiClass}>{totalGames > 0 ? totalGames : '—'}</span>}
      />
      <StatTile
        label="Record"
        kpi={<span className={heroKpiClass}>{totalGames > 0 ? `${wins}–${losses}` : '—'}</span>}
      />
      <StatTile
        label="Win rate"
        kpi={
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <div className="hidden sm:block shrink-0">
              <WinRateDonut pct={winRatePct} size={32} />
            </div>
            <span className={`${heroKpiClass} shrink-0`}>
              {winRatePct != null ? `${winRatePct}%` : '—'}
            </span>
          </div>
        }
      />
      <StatTile
        label="Win streak"
        kpi={
          <span className={`${heroKpiClass} flex items-center gap-1.5`}>
            {maxWinStreak >= 3 && <span className="text-lg sm:text-xl" aria-hidden>🔥</span>}
            {totalGames > 0 ? maxWinStreak : '—'}
          </span>
        }
      />
    </div>
  );
}

// ─── Skeleton grid (shared loading state) ─────────────────────────────────────

/**
 * @param {number[]} hiddenIndexes - Which of the 5 tile slots use kpiHideBelowMd
 */
export function KpiSkeletonGrid({ hiddenIndexes = [2, 4] }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={`${homeDarkStatTile} min-h-[4.75rem] sm:min-h-[5.25rem] animate-pulse ${
            hiddenIndexes.includes(i) ? kpiHideBelowMd : 'flex flex-col'
          }`}
        />
      ))}
    </div>
  );
}
